import { v4 as uuidv4 } from "uuid";

export interface HttpClientConfig {
  /** API 根地址 */
  apiRoot: string;
  /** 获取当前认证 token（返回空字符串表示未登录）*/
  getToken: () => string;
  /** 获取 App 版本号，返回非空时添加 Version 请求头 */
  getVersion?: () => string;
  /** 4000/4001 登录失效时的回调（应只清 session + 同步鉴权状态，不要整页 reload） */
  logoutCallback?: () => void;
  /** 已登录时收到 4003 无权限的回调（通常用于 toast） */
  onForbidden?: () => void;
  /** 收到 4005 画师资格无效时的回调（工作台弹资格提示） */
  onPainterInvalid?: () => void;
  /** 开启请求/响应调试日志 */
  showLog?: boolean;
  /** 追加到每次请求的额外请求头 */
  extraHeaders?: Record<string, string>;
}

class HttpRequestDelegate {
  private config: Required<HttpClientConfig>;

  constructor(config: HttpClientConfig) {
    this.config = {
      getVersion: () => "",
      logoutCallback: () => {},
      onForbidden: () => {},
      onPainterInvalid: () => {},
      showLog: false,
      extraHeaders: {},
      ...config,
    };
  }

  /** AuthProvider 挂载后注册，以便 4000/4001 时同步 React 登录态 */
  setLogoutCallback(callback: () => void): void {
    this.config.logoutCallback = callback;
  }

  /** 运行时更新无权限回调 */
  setOnForbidden(callback: () => void): void {
    this.config.onForbidden = callback;
  }

  private buildHeaders(overwriteHeader?: Record<string, string>): Record<string, string> {
    if (overwriteHeader !== undefined) {
      return overwriteHeader;
    }

    const token = this.config.getToken();
    const version = this.config.getVersion();

    const headers: Record<string, string> = {
      "Content-Type": "application/json",
      Accept: "application/json",
      RequestId: uuidv4(),
    };

    if (token) {
      headers.Authorization = token;
    }

    if (version) {
      // 将版本号格式从 "1.6.0" 转换为 "160"
      headers.Version = version.replace(/\./g, "");
    }

    return { ...headers, ...this.config.extraHeaders };
  }

  async baseOptions(
    params: { url: string; data?: any },
    method: string,
    overwriteHeader?: Record<string, string>,
    noInterceptor?: boolean,
  ): Promise<any> {
    const { url, data } = params;
    const headers = this.buildHeaders(overwriteHeader);

    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 30000);

    const requestInit: RequestInit = {
      method,
      headers,
      signal: controller.signal,
      body: data !== undefined ? JSON.stringify(data) : undefined,
    };

    if (this.config.showLog) {
      console.debug(">>> Send request >>>", { url: this.config.apiRoot + url, ...requestInit });
    }

    try {
      const response = await fetch(this.config.apiRoot + url, requestInit);
      clearTimeout(timeoutId);

      const responseData = await response.json();

      if (this.config.showLog) {
        console.debug("<<< Response <<<", responseData);
      }

      if (noInterceptor) {
        return responseData;
      }

      // 仅 token 相关错误触发登出；业务错误 / 500 不踢登录态
      if (responseData?.status === 4000 || responseData?.status === 4001) {
        this.config.logoutCallback();
      }

      // 已登录时的无权限：只提示，不清 session
      if (responseData?.status === 4003 && this.config.getToken()) {
        this.config.onForbidden();
      }

      if (responseData?.status === 4005) {
        this.config.onPainterInvalid();
      }

      if (responseData?.status === 567) {
        return Promise.reject({ ...responseData, msg: "操作频繁，请稍后再试" });
      }

      if (responseData?.status !== 0) {
        return Promise.reject(responseData);
      }

      return responseData;
    } catch (error: any) {
      clearTimeout(timeoutId);

      if (error?.name === "AbortError") {
        return Promise.reject({
          status: -1,
          msg: "请求超时，请稍后再试",
          request_id: "",
          timestamp: 0,
          data: null,
        });
      }

      if (error?.status !== undefined) {
        return Promise.reject(error);
      }

      return Promise.reject({
        status: -1,
        msg: "服务器开小差了，请稍后再试~",
        request_id: "",
        timestamp: 0,
        data: null,
      });
    }
  }

  get<T>(
    { url }: { url: string },
    header?: Record<string, string>,
    noInterceptor?: boolean,
  ): Promise<T> {
    return this.baseOptions({ url }, "GET", header, noInterceptor);
  }

  post<T>(
    { url, data }: { url: string; data?: any },
    header?: Record<string, string>,
    noInterceptor?: boolean,
  ): Promise<T> {
    return this.baseOptions({ url, data }, "POST", header, noInterceptor);
  }

  put<T>({ url, data }: { url: string; data?: any }, header?: Record<string, string>): Promise<T> {
    return this.baseOptions({ url, data }, "PUT", header);
  }

  delete<T>(
    { url, data }: { url: string; data?: any },
    header?: Record<string, string>,
  ): Promise<T> {
    return this.baseOptions({ url, data }, "DELETE", header);
  }
}

/**
 * HTTP 客户端实例，需要先调用 initHttpClient 初始化
 * @example
 * initHttpClient({
 *   apiRoot: "https://api.example.com",
 *   getToken: () => "token",
 *   logoutCallback: () => {
 *     localStorage.clear();
 *   },
 * });
 */
let httpInstance: HttpRequestDelegate | null = null;

/**
 * 初始化 HTTP 客户端，需在任何 API 调用前执行（通常在应用入口处调用）
 */
export function initHttpClient(config: HttpClientConfig): void {
  httpInstance = new HttpRequestDelegate(config);
}

/**
 * 获取已初始化的 HTTP 客户端实例
 */
export function getHttpClient(): HttpRequestDelegate {
  if (!httpInstance) {
    throw new Error("[Furries API] HTTP 客户端未初始化，请先调用 initHttpClient 进行初始化。");
  }
  return httpInstance;
}
